import { Block } from '@news-mono/web-common'

export interface TtsParagraph {
    start: number
    end: number
    text: string
}

export interface TtsTextStructure {
    body: string
    paragraphs: TtsParagraph[]
}

export interface TtsWordInParagraph {
    globalWordIndex: number
    localStart: number
    localEnd: number
    value: string
    time: number
}

export interface TtsParagraphWithWords extends TtsParagraph {
    words: TtsWordInParagraph[]
}

/**
 * Mirrors the server-side text extraction so speech-mark offsets match what
 * Polly synthesises. Only text-rich blocks contribute to the plain body.
 */
export function extractTtsText(blocks: Block[] | undefined): TtsTextStructure {
    const paragraphs: TtsParagraph[] = []
    let body = ''

    if (!blocks || blocks.length === 0) {
        return { body, paragraphs }
    }

    for (const block of blocks) {
        let blockText = ''

        switch (block.kind) {
            case 'text':
            case 'subhead':
                if (typeof block.text === 'string' && block.text.trim()) {
                    blockText = block.text.trim()
                }
                break
            case 'ordered-list':
            case 'unordered-list': {
                const items = Array.isArray(block.items) ? block.items : []
                const itemTexts = items
                    .map((item) => {
                        if (!item || item.kind !== 'list-item' || !Array.isArray(item.blocks))
                            return ''
                        const parts = item.blocks
                            .map((b) => {
                                if (b.kind === 'text' || b.kind === 'subhead') {
                                    return typeof b.text === 'string' && b.text.trim()
                                        ? b.text.trim()
                                        : ''
                                }
                                return ''
                            })
                            .filter((s) => s.length > 0)
                        return parts.join(' ')
                    })
                    .filter((s) => s.length > 0)
                if (itemTexts.length > 0) {
                    // Keep `body` identical (space-joined items), but split paragraphs per item
                    // so highlights/anchors can map to individual rendered list items.
                    for (const itemText of itemTexts) {
                        if (body.length > 0) body += ' '
                        const start = body.length
                        body += itemText
                        paragraphs.push({ start, end: body.length, text: itemText })
                    }
                }
                break
            }
            default:
                break
        }

        if (blockText) {
            if (body.length > 0) {
                body += ' '
            }
            const start = body.length
            body += blockText
            paragraphs.push({ start, end: body.length, text: blockText })
        }
    }

    return { body, paragraphs }
}

/**
 * Preprocesses speech marks so each paragraph gets the words whose positions
 * are aligned directly against the flattened article body text.
 *
 * High‑level idea:
 * - Treat `ttsText.body` (from `extractTtsText`) as the canonical text that Polly
 *   actually read.
 * - Use Polly's word sequence (value + time) only for ordering and timing.
 * - Derive the character offsets we use for highlighting by matching each word
 *   back into the canonical body text, instead of trusting Polly's raw start/end
 *   offsets for paragraph assignment.
 *
 * Algorithm:
 * 1. Build `body` and paragraph ranges via `extractTtsText`.
 * 2. Sort all Polly `word` marks by time and assign a monotonic `wordIndex`.
 * 3. For each word (in time order), try to match its `value` in `body`:
 *    - Start searching from the end of the previous match (`searchPos`).
 *    - First use a word‑boundary regex to find the next full‑word occurrence.
 *    - If that fails, fall back to `indexOf` from `searchPos`.
 *    - If that still fails (e.g. minor text drift), fall back to Polly's original
 *      `start` offset as a best‑effort position.
 *    This produces a monotonically increasing `bodyStart/bodyEnd` for each word.
 * 4. Assign each canonical word to a paragraph based on `bodyStart`:
 *    - For paragraph i, the ownership interval is
 *      `[paragraphs[i].start, paragraphs[i+1].start)`, and for the last paragraph
 *      `[last.start, +∞)`.
 *    - Any word whose `bodyStart` falls in that interval is considered part of
 *      that paragraph and gets a `localStart/localEnd` relative to it.
 *
 * Benefits:
 * - Highlighting is driven by the actual rendered article text, not by Polly's
 *   raw offsets, which can drift slightly at sentence/paragraph boundaries.
 * - Sentences are implicit: a Polly sentence may span multiple paragraphs or
 *   several sentences may live in one paragraph, but all words are still mapped
 *   correctly by position.
 * - As long as the words exist in `body` in the right order, even the last few
 *   words of a paragraph are reliably assigned to the correct paragraph.
 *
 * @param speechMarks - All speech marks from Polly (words and sentences)
 * @param ttsText - The extracted TTS text structure with paragraph boundaries
 * @returns Array of paragraphs with their words in local coordinates
 */
export function preprocessTtsData(
    speechMarks: Array<{ type: string; start?: number; end?: number; time?: number; value?: string }>,
    ttsText: TtsTextStructure,
): TtsParagraphWithWords[] {
    if (!speechMarks || speechMarks.length === 0 || !ttsText.paragraphs?.length) {
        return []
    }

    const paragraphs = ttsText.paragraphs
    const body = ttsText.body || ''

    // 1) Collect and sort all Polly word marks by time, and attach a global wordIndex
    const wordMarks = speechMarks
        .filter(
            (m) =>
                m.type === 'word' &&
                typeof m.start === 'number' &&
                typeof m.end === 'number' &&
                typeof m.time === 'number' &&
                typeof m.value === 'string',
        )
        .sort((a, b) => (a.time ?? 0) - (b.time ?? 0))
        .map((mark, wordIndex) => ({ ...mark, wordIndex }))

    // 2) Align each word against the canonical body text to derive stable offsets
    type CanonicalWord = typeof wordMarks[number] & { bodyStart: number; bodyEnd: number }
    const canonicalWords: CanonicalWord[] = []

    const wordBoundaryRegexCache = new Map<string, RegExp>()

    let searchPos = 0
    for (const mark of wordMarks) {
        const value = mark.value!
        let bodyStart = -1

        if (body.length > 0 && value.length > 0) {
            // Prefer word-boundary regex first so we only match whole words
            let wordBoundaryRegex = wordBoundaryRegexCache.get(value)
            if (!wordBoundaryRegex) {
                const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                wordBoundaryRegex = new RegExp(`\\b${escaped}\\b`, 'g')
                wordBoundaryRegexCache.set(value, wordBoundaryRegex)
            }
            wordBoundaryRegex.lastIndex = searchPos
            const match = wordBoundaryRegex.exec(body)
            if (match) {
                bodyStart = match.index
            } else {
                // Fallback: simple indexOf from the current search position
                const idx = body.indexOf(value, searchPos)
                if (idx !== -1) {
                    bodyStart = idx
                }
            }
        }

        if (bodyStart === -1) {
            // Final fallback: use Polly's original start (may drift slightly, but is monotonic)
            bodyStart = typeof mark.start === 'number' ? mark.start : searchPos
        }

        const bodyEnd = bodyStart + value.length
        canonicalWords.push({ ...(mark as any), bodyStart, bodyEnd })
        // Next search starts from the end of this match so repeated words map to subsequent occurrences
        searchPos = Math.max(searchPos, bodyEnd)
    }

    // 3) Assign each canonical word to a paragraph based on bodyStart:
    //    para[i].start <= bodyStart < para[i+1].start, and for the last paragraph bodyStart >= last.start
    const result: TtsParagraphWithWords[] = []
    const wordsByParagraph: TtsWordInParagraph[][] = paragraphs.map(() => [])

    for (const mark of canonicalWords) {
        const wStart = mark.bodyStart
        const wEnd = mark.bodyEnd

        // Determine which paragraph owns this word: [para.start, para.end)
        let paraIndex = paragraphs.length - 1
        for (let i = 0; i < paragraphs.length; i++) {
            const current = paragraphs[i]
            if (wStart >= current.start && wStart < current.end) {
                paraIndex = i
                break
            }
        }

        const para = paragraphs[paraIndex]
        wordsByParagraph[paraIndex].push({
            globalWordIndex: mark.wordIndex,
            localStart: Math.max(0, wStart - para.start),
            localEnd: Math.min(wEnd - para.start, para.end - para.start),
            value: mark.value!,
            time: mark.time!,
        })
    }

    // 4) Build the paragraph array with their aligned words
    paragraphs.forEach((para, idx) => {
        result.push({
            start: para.start,
            end: para.end,
            text: para.text,
            words: wordsByParagraph[idx],
        })
    })

    return result
}
